import { Delegate } from './delegate.js';
import { subscribeToKbiteNotify } from './kbite-notify-api.js';

/**
 * @param {HTMLElement} trigger
 * @returns {{ productId: string, productHandle: string, variantId: string, productUrl: string }|null}
 */
export function resolveNotifyContext(trigger) {
  if (trigger.dataset.productId) {
    return {
      productId: trigger.dataset.productId,
      productHandle: trigger.dataset.productHandle || '',
      variantId: trigger.dataset.variantId || '',
      productUrl: trigger.dataset.productUrl?.split('?')[0] || '',
    };
  }

  let productInfo = trigger.closest('product-info');
  if (!productInfo) {
    const stickyBar = trigger.closest('sticky-atc-bar, sticky-atc-bar-minimalist');
    if (stickyBar?.dataset.productId) {
      productInfo = document.querySelector(`product-info[data-product-id="${stickyBar.dataset.productId}"]`);
    }
  }

  if (!productInfo) return null;

  const productUrl = productInfo.dataset.url?.split('?')[0] || '';
  const productId = productInfo.dataset.productId || '';
  const productHandle =
    productInfo.dataset.productHandle || productUrl.match(/\/products\/([^/?#]+)/)?.[1] || '';
  const variantInput = productInfo.querySelector('.product-variant-id, input[name="id"]');
  const variantId = variantInput?.value || '';

  if (!productId || !variantId) return null;

  return { productId, productHandle, variantId, productUrl };
}

/**
 * @param {string} productUrl
 * @param {string} productHandle
 * @returns {string}
 */
function resolveProductHandle(productUrl, productHandle) {
  if (productHandle) return productHandle;
  return productUrl.match(/\/products\/([^/?#]+)/)?.[1] || '';
}

/**
 * @param {HTMLFormElement} form
 * @returns {{ productId: string, productHandle: string, variantId: string, productUrl: string }|null}
 */
function readNotifyContextFromForm(form) {
  const productId = form.dataset.notifyProductId || '';
  const variantId = form.dataset.notifyVariantId || '';
  if (!productId || !variantId) return null;

  return {
    productId,
    productHandle: form.dataset.notifyProductHandle || '',
    variantId,
    productUrl: form.dataset.notifyProductUrl || '',
  };
}

class ProductCardNotify extends HTMLElement {
  #modal = null;
  #bodyDelegate = null;
  #modalDelegate = null;
  #productId = null;
  #productHandle = null;
  #variantId = null;
  #productUrl = null;
  #themeCampaignId = '';
  #submitting = false;

  connectedCallback() {
    this.#modal = this.querySelector('#ProductCardNotifyModal') || this.querySelector('basic-modal');
    if (!this.#modal) {
      console.warn('[product-card-notify] Modal not found — handlers not bound');
      return;
    }

    this.#themeCampaignId = this.dataset.kbiteCampaignId || window.FoxTheme?.settings?.pcardKbiteCampaignId || '';

    this.#bodyDelegate = new Delegate(document.body);
    this.#bodyDelegate.on('click', '[data-action="open-product-card-notify"]', this.#onOpen);
    this.#bodyDelegate.on('keypress', '[data-action="open-product-card-notify"]', this.#onOpenKeypress);
    this.#bodyDelegate.on('click', '[data-action="open-kbite-notify"]', this.#onOpenKbiteNotify);
    this.#bodyDelegate.on('keypress', '[data-action="open-kbite-notify"]', this.#onOpenKbiteNotifyKeypress);

    this.#modalDelegate = new Delegate(this.#modal);
    this.#modalDelegate.on(
      'click',
      '[data-action="product-card-notify-submit"]',
      this.#onSubmit,
      true
    );
    this.#modalDelegate.on('submit', '[data-product-card-notify-form]', this.#onFormSubmit, true);

    this.#modal.addEventListener('modal:handleAfterHide', this.#onModalAfterHide);
  }

  disconnectedCallback() {
    this.#modalDelegate?.destroy();
    this.#bodyDelegate?.destroy();
    this.#modal?.removeEventListener('modal:handleAfterHide', this.#onModalAfterHide);
    this.#modalDelegate = null;
    this.#bodyDelegate = null;
  }

  #getModal() {
    return document.getElementById('ProductCardNotifyModal') || this.#modal;
  }

  #storeContextOnForm(form, context) {
    form.dataset.notifyProductId = context.productId;
    form.dataset.notifyVariantId = context.variantId;
    form.dataset.notifyProductHandle = context.productHandle || '';
    form.dataset.notifyProductUrl = context.productUrl || '';
  }

  #syncContextFromForm(form) {
    const stored = readNotifyContextFromForm(form);
    if (!stored) return;

    this.#productId = stored.productId;
    this.#variantId = stored.variantId;
    this.#productHandle = stored.productHandle;
    this.#productUrl = stored.productUrl;
  }

  /**
   * @param {{ productId: string, productHandle: string, variantId: string, productUrl: string }} context
   * @param {HTMLElement|null} [trigger]
   * @returns {Promise<void>}
   */
  async openWithContext(context, trigger = null) {
    if (!this.#modal || !context?.productId || !context?.variantId) return;

    this.#productId = context.productId;
    this.#productHandle = resolveProductHandle(context.productUrl, context.productHandle);
    this.#variantId = context.variantId;
    this.#productUrl = context.productUrl;

    this.#resetForm();

    const form = this.#getModal()?.querySelector('[data-product-card-notify-form]');
    if (form) this.#storeContextOnForm(form, context);

    this.#getModal()?.show?.(trigger);
  }

  #onSubmit = (event, matched) => {
    const trigger =
      matched?.matches?.('[data-action="product-card-notify-submit"]')
        ? matched
        : event.target.closest('[data-action="product-card-notify-submit"]');
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();

    const form = trigger.closest('[data-product-card-notify-form]');
    if (form) void this.#handleSubscribe(form);
  };

  #onFormSubmit = (event, matched) => {
    const form =
      matched?.matches?.('[data-product-card-notify-form]')
        ? matched
        : event.target.closest('[data-product-card-notify-form]');
    if (!form) return;

    event.preventDefault();
    event.stopPropagation();
    void this.#handleSubscribe(form);
  };

  #onOpenKeypress = (event) => {
    if (event.key !== 'Enter') return;
    void this.#onOpen(event);
  };

  #onOpenKbiteNotifyKeypress = (event) => {
    if (event.key !== 'Enter') return;
    void this.#onOpenKbiteNotify(event);
  };

  #onOpen = async (event, matched) => {
    const trigger =
      matched?.matches?.('[data-action="open-product-card-notify"]')
        ? matched
        : event.target.closest('[data-action="open-product-card-notify"]');
    if (!trigger) return;

    event.preventDefault();
    const context = resolveNotifyContext(trigger);
    if (!context) return;

    await this.openWithContext(context, trigger);
  };

  #onOpenKbiteNotify = async (event, matched) => {
    const trigger =
      matched?.matches?.('[data-action="open-kbite-notify"]')
        ? matched
        : event.target.closest('[data-action="open-kbite-notify"]');
    if (!trigger || trigger.hasAttribute('hidden')) return;

    event.preventDefault();
    const context = resolveNotifyContext(trigger);
    if (!context) return;

    await this.openWithContext(context, trigger);
  };

  #onModalAfterHide = () => {
    this.#resetForm();
  };

  /**
   * @param {HTMLFormElement} form
   * @returns {Promise<void>}
   */
  async #handleSubscribe(form) {


    if (this.#submitting) {
      return;
    }

    this.#syncContextFromForm(form);

    if (!this.#productId || !this.#variantId) {
      console.warn('[product-card-notify] Missing product context for subscribe', {
        productId: this.#productId,
        variantId: this.#variantId,
      });
      return;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const modal = this.#getModal();
    const emailInput = form.querySelector('.email-input, input[type="email"]');
    const submitButton = form.querySelector('[data-action="product-card-notify-submit"]');
    const marketingInput = modal?.querySelector('.marketing-container-input');
    const email = emailInput?.value?.trim();
    if (!email) return;

    const optInToMarketing = Boolean(marketingInput?.checked);
    const productHandle = resolveProductHandle(this.#productUrl, this.#productHandle);

    this.#submitting = true;
    submitButton?.setAttribute('aria-disabled', 'true');
    submitButton?.setAttribute('aria-busy', 'true');
    submitButton?.classList.add('btn--loading');

    try {
      const result = await subscribeToKbiteNotify({
        email,
        productId: this.#productId,
        variantId: this.#variantId,
        productHandle,
        productUrl: this.#productUrl,
        optInToMarketing,
        themeCampaignId: this.#themeCampaignId,
      });

      const shouldShowSuccess = Boolean(result?.success) || Boolean(result?.ok);

      if (shouldShowSuccess) {
        this.#showSuccess();
        return;
      }

      console.warn('[product-card-notify] Subscribe failed:', result?.response?.status ?? result);
    } catch (error) {
      console.warn('[product-card-notify] Subscribe failed:', error);
    } finally {
      this.#submitting = false;
      submitButton?.removeAttribute('aria-disabled');
      submitButton?.setAttribute('aria-busy', 'false');
      submitButton?.classList.remove('btn--loading');
    }
  }

  #resetForm() {
    const modal = this.#getModal();
    const form = modal?.querySelector('[data-product-card-notify-form]');
    const marketingInput = modal?.querySelector('.marketing-container-input');
    form?.reset();
    if (marketingInput) marketingInput.checked = false;
    this.#hideSuccess();
  }

  #showSuccess() {
    const modal = this.#getModal();
    const message = modal?.querySelector('[data-product-card-notify-success]');

    if (!message) {
      console.warn('[product-card-notify] Success element not found in modal');
      return;
    }

    message.removeAttribute('hidden');
    message.setAttribute('aria-hidden', 'false');
  }

  #hideSuccess() {
    const modal = this.#getModal();
    const message = modal?.querySelector('[data-product-card-notify-success]');

    if (!message) return;

    message.setAttribute('hidden', '');
    message.setAttribute('aria-hidden', 'true');
  }
}

if (!customElements.get('product-card-notify')) {
  customElements.define('product-card-notify', ProductCardNotify);
}

/**
 * @param {{ productId: string, productHandle: string, variantId: string, productUrl: string }} context
 * @param {HTMLElement|null} [trigger]
 * @returns {Promise<void>}
 */
export function openNotifyModal(context, trigger = null) {
  const host = document.querySelector('product-card-notify');
  return host?.openWithContext(context, trigger);
}
